import { useCallback, useState } from "react";
import type { AxiosError } from "axios";
import { Api } from "services/api/api.service";
import type { Result } from "utils/result";
import type {
  TimeReportBackendErrorResponse,
  TimeReportErrorKey,
} from "hooks/timeReportErrors";
import { resolveAlertKeyFromApiError } from "hooks/timeReportErrors";

export interface WithdrawTimeReportPayload {
  companyId: number;
  timeReportId: number;
  companyNotes: string;
}

export interface UseWithdrawTimeReportResult {
  withdrawTimeReport: (
    payload: WithdrawTimeReportPayload,
  ) => Promise<Result<void, TimeReportErrorKey>>;
  isLoading: boolean;
  error: TimeReportErrorKey | null;
}

export const useWithdrawTimeReport = (): UseWithdrawTimeReportResult => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<TimeReportErrorKey | null>(null);

  const withdrawTimeReport = useCallback(
    (
      payload: WithdrawTimeReportPayload,
    ): Promise<Result<void, TimeReportErrorKey>> => {
      setIsLoading(true);
      setError(null);
      return Api()
        .company.timeReports.withdrawTimeReport(
          payload.companyId,
          payload.timeReportId,
          payload.companyNotes,
        )
        .then(
          (): Result<void, TimeReportErrorKey> => ({
            ok: true,
            value: undefined,
          }),
        )
        .catch(
          (
            error: AxiosError<TimeReportBackendErrorResponse>,
          ): Result<void, TimeReportErrorKey> => {
            const alertKey = resolveAlertKeyFromApiError(error);
            setError(alertKey);
            return { ok: false, error: alertKey };
          },
        )
        .finally(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  return { withdrawTimeReport, isLoading, error };
};
